/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { dialogComponents } from 'components/Dialog/dialogComponents';
import { useModalActions } from 'components/ModalManager/Context';
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'components/Button';
import { noop } from 'utils/fp';
import { ScopeMatcher, useCurrentUserId, useCurrentUser } from 'modules/Auth/hooks';
import { useMutation } from 'react-query';
import { useRouter } from 'next/router';
import { deploymentsApiEffects } from 'api/deployments/apiEffects';
import { getDeployModelRequestBody, createFourDigitRandomString } from 'modules/Compute/utils';
import { DeploymentFormValues } from 'modules/Compute/types';
import { RadioGroup, useRadioState } from 'components/RadioGroup';
import { SearchInput } from 'components/Search/SearchInput';
import { uniq } from 'rambda';
import { FeaturedFlagsUnion } from 'modules/FeaturedFlags/FeaturedFlagsContext';
import { HUBSPOT_CUSTOM_EVENTS_NAME, sendEvent } from 'utils/hubspotApi/hubspotApi';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { SetupComputeMockData, TrendingModel } from '../constants';
import { radioText, radioGroupStyles, search } from './EasyDeployModal.styles';
import { modalFooter, titleStyles, titleWrapper } from './SetupComputeModal.styles';
import { RestrictBillingPlan, UpgradePlanWrapper } from './RestrictBillingPlan';

type EasyDeployModalProps = {
  closeAction: () => void;
  clusterId: string;
  nodepoolId: string;
  regionId: string;
  providerId: string;
  computeType: string;
  modelsList: TrendingModel[];
  /**
   * Callback to use instead of defaulting to closeAction()
   */
  onCancel?: () => void;
};

const EASY_DEPLOY_MODAL_ID = 'easy_deploy_modal';

function getModelFromId(modelId: string, modelsList: TrendingModel[]) {
  return modelsList.find((data) => data.modelId === modelId);
}

function FormatOption(props: TrendingModel) {
  return (
    <>
      <props.icon size={24} />
      <span className={radioText}>{props.title}</span>
    </>
  );
}

export function EasyDeployModal({
  clusterId,
  nodepoolId,
  regionId,
  providerId,
  computeType,
  modelsList,
  closeAction,
  onCancel,
}: EasyDeployModalProps) {
  const userId = useCurrentUserId();
  const user = useCurrentUser();
  const { track } = useDataCollection();
  const router = useRouter();
  const [searchModelText, setSearchModelText] = useState<string>('');
  const radioState = useRadioState({ state: modelsList[0]?.modelId });

  const filteredModelsList = useMemo(
    () =>
      modelsList.filter((model) => {
        return model.title.toLowerCase().includes(searchModelText.toLowerCase());
      }),
    [searchModelText, modelsList],
  );

  const computeTypeDetails = useMemo(
    () =>
      getModelFromId(radioState.state as string, modelsList)
        ?.supportedComputeTypes.map((computeType) => {
          return SetupComputeMockData.find((data) => data.setupComputeType === computeType);
        })
        .reduce(
          (scopesAndFlags, computeType) => {
            if (!computeType) {
              return scopesAndFlags;
            }

            return {
              requiredFeaturedFlags: uniq(scopesAndFlags?.requiredFeaturedFlags?.concat(...(computeType?.requiredFeaturedFlags || []))),
              requiredScopes: uniq(scopesAndFlags?.requiredScopes?.concat(...(computeType?.requiredScopes || []))),
            };
          },
          { requiredScopes: [] as ScopeMatcher[], requiredFeaturedFlags: [] as FeaturedFlagsUnion[] },
        ),
    [radioState.state],
  );

  const { mutate, isLoading } = useMutation(
    async (deployModelId: string) => {
      // Create a unique deployment ID:
      const deployModelIdSlug = deployModelId?.toLowerCase().replace(/[^\w-]/g, '');
      const deploymentId = `deploy-${deployModelIdSlug}-${createFourDigitRandomString()}`;
      const deployModel = getModelFromId(deployModelId, modelsList);

      if (!deployModel) {
        return null;
      }

      const requestBody = getDeployModelRequestBody({
        isEditing: false,
        userId,
        values: {
          clusterId: { value: clusterId, name: clusterId },
          nodePoolId: { value: nodepoolId, name: nodepoolId },
          model: { id: deployModel.modelId, app_id: deployModel.appId, user_id: deployModel.userId } as CF.API.Models.Model,
          deploymentId,
          deployLatestVersion: true,
        } as DeploymentFormValues,
      });

      const isModelDeployed = await deploymentsApiEffects.createDeployment(userId, requestBody);

      if (!isModelDeployed) {
        return null;
      }

      return { clusterId, nodepoolId, modelId: deployModel.modelId, modelVersionId: deployModel.modelVersionId, userId: deployModel.userId };
    },
    {
      onSuccess: (data) => {
        if (data) {
          track('Model deployed successfully', {
            clusterId: data.clusterId,
            nodepoolId: data.nodepoolId,
            compute_type: computeType,
            compute_region: regionId,
            compute_provider: providerId,
            model_id: data.modelId,
            model_user_id: data.userId,
            model_version_id: data.modelVersionId,
          });
          sendEvent({
            email: user?.email,
            eventName: HUBSPOT_CUSTOM_EVENTS_NAME.coModelDeployed,
            properties: {
              compute_type: computeType,
              compute_region: regionId,
              compute_provider: providerId,
              model_id: data.modelId,
              model_user_id: data.userId,
              model_version_id: data.modelVersionId,
            },
          });
          router.push(`/compute/clusters/${encodeURIComponent(data.clusterId)}/nodepools/${data.nodepoolId}`);
        }
      },
    },
  );

  return (
    <RestrictBillingPlan
      requiredScopes={computeTypeDetails?.requiredScopes}
      requiredFeaturedFlags={computeTypeDetails?.requiredFeaturedFlags}
      closeAction={closeAction}
    >
      <div>
        <div className={titleWrapper}>
          <h3 className={titleStyles}>Select a model to deploy</h3>
          <dialogComponents.CloseButton onClick={closeAction} />
        </div>

        <SearchInput
          placeholder="Search"
          value={searchModelText}
          setValue={setSearchModelText}
          wrapperClassName={search}
          inputClassName="input"
          iconClassName="icon"
        />

        <RadioGroup
          radio={radioState}
          className={radioGroupStyles}
          radioListClass="radio-list"
          options={filteredModelsList.map((data) => ({
            label: <FormatOption {...data} />,
            value: data.modelId,
          }))}
        />

        <div className={modalFooter}>
          <Button size="md" variant="tertiary" onClick={onCancel || closeAction}>
            Cancel
          </Button>
          <UpgradePlanWrapper>
            <Button
              size="md"
              loading={isLoading}
              disabled={isLoading || !radioState.state}
              onClick={() => radioState.state && mutate(radioState.state as string)}
            >
              Deploy model
            </Button>
          </UpgradePlanWrapper>
        </div>
      </div>
    </RestrictBillingPlan>
  );
}

export function useEasyDeployModal() {
  const { openModal, closeModal } = useModalActions();

  return useCallback((props: Omit<EasyDeployModalProps, 'closeAction'>) => {
    openModal({
      id: EASY_DEPLOY_MODAL_ID,
      title: '',
      content: <EasyDeployModal {...props} closeAction={() => closeModal({ id: EASY_DEPLOY_MODAL_ID })} />,
      makeActions: noop,
    });
  }, []);
}
